// constants/serviceColors.ts

/**
 * Configuração centralizada de cores para os serviços de sinalização
 * Garante consistência visual entre Mapbox e ImageViewer
 */

export interface ServiceColorConfig {
  id: number;
  name: string;
  color: string;
  opacity: number;
  rgbaColor: string; // Para uso direto
}

// Função auxiliar para converter hex + opacity em rgba

// ✅ Definição única das cores por serviço
export const SERVICE_COLORS: Record<number, ServiceColorConfig> = {
  1: {
    id: 1,
    name: 'Implantação',
    color: '#22c55e', // Verde
    opacity: 1.0,
    rgbaColor: 'rgba(34, 197, 94, 1.0)'
  },
  2: {
    id: 2,
    name: 'Remoção',
    color: '#ef4444', // Vermelho
    opacity: 1.0,
    rgbaColor: 'rgba(239, 68, 68, 1.0)'
  },
  3: {
    id: 3,
    name: 'Limpeza',
    color: '#3b82f6', // Ciano/Azul
    opacity: 0.6,
    rgbaColor: 'rgba(59, 130, 246, 0.6)'
  },
  4: {
    id: 4,
    name: 'Manter',
    color: '#fbbf24', // Amarelo
    opacity: 0.75,
    rgbaColor: 'rgba(251, 191, 36, 0.75)'
  }
};

// ✅ Cor padrão para serviços não mapeados
export const DEFAULT_SERVICE_COLOR: ServiceColorConfig = {
  id: 0,
  name: 'Desconhecido',
  color: '#9ca3af', // Cinza
  opacity: 0.5,
  rgbaColor: 'rgba(156, 163, 175, 0.5)'
};

/**
 * Obtém a configuração de cor para um serviço específico
 */
export const getServiceColor = (
  serviceId: number | undefined | null
): ServiceColorConfig => {
  if (!serviceId) return DEFAULT_SERVICE_COLOR;
  return SERVICE_COLORS[serviceId] || DEFAULT_SERVICE_COLOR;
};

/**
 * Obtém apenas a cor hex para um serviço
 */
export const getServiceHexColor = (
  serviceId: number | undefined | null
): string => {
  return getServiceColor(serviceId).color;
};

/**
 * Obtém a cor rgba para um serviço
 */
export const getServiceRgbaColor = (
  serviceId: number | undefined | null
): string => {
  return getServiceColor(serviceId).rgbaColor;
};

/**
 * Obtém a opacidade para um serviço
 */
export const getServiceOpacity = (
  serviceId: number | undefined | null
): number => {
  return getServiceColor(serviceId).opacity;
};

/**
 * Gera expressão de cor para Mapbox baseada em service_id
 * Retorna formato: ['match', ['get', 'service_id'], 1, '#color1', 2, '#color2', ...]
 */
export const getMapboxColorExpression = () => {
  const expression: any[] = ['match', ['get', 'service_id']];

  Object.values(SERVICE_COLORS).forEach(config => {
    expression.push(config.id, config.color);
  });

  // Cor padrão
  expression.push(DEFAULT_SERVICE_COLOR.color);

  return expression;
};

/**
 * Gera expressão de opacidade para Mapbox baseada em service_id
 */
export const getMapboxOpacityExpression = () => {
  const expression: any[] = ['match', ['get', 'service_id']];

  Object.values(SERVICE_COLORS).forEach(config => {
    expression.push(config.id, config.opacity);
  });

  // Opacidade padrão
  expression.push(DEFAULT_SERVICE_COLOR.opacity);

  return expression;
};

/**
 * Lista todas as cores disponíveis (útil para legendas)
 */
export const getAllServiceColors = (): ServiceColorConfig[] => {
  return Object.values(SERVICE_COLORS);
};
